import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useBehaviorSubject } from '@euler/utils/hooks/useBehaviorSubject';
import { Lightbulb, Send } from 'lucide-react-native';
import { memo, useEffect, useState } from 'react';
import {
  Keyboard,
  Platform,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { aiColors, tint } from '../../../../../ai-suite/theme';
import { HoldToTalk } from './HoldToTalk.web';
import { PracticeComposerProps } from './practiceComposerTypes';

/**
 * The trainee's controls: 💡 for a hint, a box to type in, and the mic to
 * hold and talk; with words in the box the mic becomes send.
 */
export const PracticeComposer = memo(
  ({
    canType,
    canTalk,
    initial,
    onSend,
    onActivity,
    onHint,
    hintShown,
    voice,
  }: PracticeComposerProps) => {
    const [voiceState] = useBehaviorSubject(voice.state$);
    const talking = voiceState.kind === 'holding';
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const [text, setText] = useState(initial);
    // A line that did not go comes back to the box, without remounting the
    // mic under a finger still holding it.
    useEffect(() => {
      if (initial) setText(initial);
    }, [initial]);
    // Above the keyboard the home indicator's inset is not needed.
    const [keyboard, setKeyboard] = useState(false);
    useEffect(() => {
      if (Platform.OS !== 'ios') return;
      const shown = Keyboard.addListener('keyboardWillShow', () =>
        setKeyboard(true),
      );
      const hidden = Keyboard.addListener('keyboardWillHide', () =>
        setKeyboard(false),
      );
      return () => {
        shown.remove();
        hidden.remove();
      };
    }, []);
    const typed = !!text.trim();
    const send = () => {
      if (!canType || !typed) return;
      onSend(text, null);
      setText('');
    };
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(8),
          paddingHorizontal: rpx(12),
          paddingTop: rpx(12),
          paddingBottom: (keyboard ? 0 : insets.bottom) + rpx(12),
          borderTopWidth: 1,
          borderTopColor: aiColors.border,
          backgroundColor: aiColors.card,
        }}
      >
        {onHint ? (
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel="提示"
            accessibilityState={{ selected: hintShown }}
            disabled={!canType}
            onPress={onHint}
            style={{
              width: rpx(40),
              height: rpx(40),
              borderRadius: rpx(20),
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: hintShown
                ? tint(aiColors.primary, 0.12)
                : aiColors.muted,
              opacity: canType ? 1 : 0.5,
            }}
          >
            <Lightbulb
              size={rpx(20)}
              color={hintShown ? aiColors.primary : aiColors.mutedForeground}
            />
          </TouchableOpacity>
        ) : null}
        <View
          style={{
            flex: 1,
            minWidth: 0,
            height: rpx(44),
            justifyContent: 'center',
            borderRadius: rpx(22),
            paddingHorizontal: rpx(16),
            backgroundColor: aiColors.input,
          }}
        >
          <TextInput
            value={text}
            onChangeText={value => {
              setText(value);
              onActivity();
            }}
            onSubmitEditing={send}
            returnKeyType="send"
            blurOnSubmit={false}
            placeholder="输入你要说的话…"
            placeholderTextColor={aiColors.mutedForeground}
            style={{
              fontSize: rpx(14),
              color: aiColors.foreground,
              padding: 0,
            }}
          />
        </View>
        {typed ? (
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel="发送"
            disabled={!canType}
            onPress={send}
            style={{
              width: rpx(44),
              height: rpx(44),
              borderRadius: rpx(22),
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: aiColors.primary,
              opacity: canType ? 1 : 0.4,
            }}
          >
            <Send size={rpx(18)} color={aiColors.primaryForeground} />
          </TouchableOpacity>
        ) : (
          <HoldToTalk
            enabled={canTalk}
            talking={talking}
            level$={voice.level$}
            onPress={() => voice.press()}
            onRelease={() => voice.release()}
            onCancel={voice.cancel}
          />
        )}
      </View>
    );
  },
);
